import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import EditCategoryModal from "./EditCategoryModal";
import Table from "../../common/Table";
import Modal from "../../common/Modal";
import FormInput from "../../common/FormInput";
import { useFetch, useSubmission } from "../../../hooks/useCallApi";

const ComplaintsCategoriesModal = ({ isOpen, onClose }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [createErrors, setCreateErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    priority: "",
    status: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  const params = useMemo(
    () => ({
      per_page: perPage,
      page: currentPage,
      search: searchQuery || undefined,
    }),
    [perPage, currentPage, searchQuery],
  );

  const extractDataArray = (response) => {
    if (!response) return [];

    console.log("Extracting data from response:", response);

    if (
      response.data &&
      response.data.complaint_categories &&
      Array.isArray(response.data.complaint_categories)
    ) {
      return response.data.complaint_categories;
    }

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }

    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data)
    ) {
      return response.data.data;
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (
      response.data &&
      response.data.items &&
      Array.isArray(response.data.items)
    ) {
      return response.data.items;
    }
    if (
      response.data &&
      response.data.records &&
      Array.isArray(response.data.records)
    ) {
      return response.data.records;
    }

    if (response.data && typeof response.data === "object") {
      const firstArrayProp = Object.values(response.data).find((val) =>
        Array.isArray(val),
      );
      if (firstArrayProp) {
        return firstArrayProp;
      }
    }

    return [];
  };

  const {
    isLoading,
    refetch,
  } = useFetch(
    ["complaints-categories", params],
    (api) => api.admin.complaints.getCategory(params),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      enabled: isOpen,
      onSuccess: (response) => {
        console.log("API Response:", response);
        const categoriesData = extractDataArray(response);
        console.log("Extracted Categories:", categoriesData);
        setAllData(categoriesData);
      },
      onError: (err) => {
        console.error("API Error:", err);
        setAllData([]);
        setFilteredData([]);
        setTableData([]);
        setTotalItems(0);
        setLastPage(1);
      },
    },
  );

  const { mutate: createCategory, isLoading: isCreating } = useSubmission(
    ["create-category"],
    (api, data) => api.admin.complaints.createCategory(data),
    {
      onSuccess: (response) => {
        console.log("Category created successfully:", response);
        setShowCreateModal(false);
        setFormData({
          name: "",
          description: "",
          priority: "",
          status: "",
        });
        setCreateErrors({});
        
        setCurrentPage(1);
        refetch();
      },
      onError: (error) => {
        console.error("Error creating category:", error);
        if (error?.response?.data?.errors) {
          setCreateErrors(error.response.data.errors);
        }
      },
    }
  );

  const { mutate: deleteCategory, isLoading: isDeleting } = useSubmission(
    ["delete-category"],
    (api, id) => api.admin.complaints.deleteCategory(id),
    {
      onSuccess: () => {
        console.log("Category deleted successfully");
        
        const newTotalItems = totalItems - 1;
        const newLastPage = Math.max(1, Math.ceil(newTotalItems / perPage));
        
        if (currentPage > newLastPage) {
          setCurrentPage(newLastPage);
        }
        
        refetch();
      },
      onError: (error) => {
        console.error("Error deleting category:", error);
      },
    }
  );

  const { mutate: toggleStatus, isLoading: isToggling } = useSubmission(
    ["toggle-status"],
    (api, { id, data }) => api.admin.complaints.updateCategory(id, data),
    {
      onSuccess: () => {
        console.log("Category status updated successfully");
        refetch();
      },
      onError: (error) => {
        console.error("Error updating category status:", error);
      },
    }
  );

  const applyFilters = useCallback(
    (data) => {
      const dataArray = Array.isArray(data) ? data : [];

      if (dataArray.length === 0) {
        setFilteredData([]);
        setTableData([]);
        setTotalItems(0);
        setLastPage(1);
        return;
      }

      let filtered = [...dataArray];

      const searchTerm = searchQuery?.trim().toLowerCase();

      if (searchTerm) {
        filtered = filtered.filter((item) => {
          if (!item) return false;

          const name = (item.name || "").toLowerCase();
          const description = (item.description || "").toLowerCase();

          return name.includes(searchTerm) || description.includes(searchTerm);
        });
      }

      setFilteredData(filtered);

      const total = filtered.length;
      setTotalItems(total);

      const totalPages = Math.max(1, Math.ceil(total / perPage));
      setLastPage(totalPages);

      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      } else {
        setTableData(filtered.slice((currentPage - 1) * perPage, currentPage * perPage));
      }
    },
    [searchQuery, perPage, currentPage],
  );

  useEffect(() => {
    if (allData.length > 0) {
      applyFilters(allData);
    } else {
      setFilteredData([]);
      setTableData([]);
      setTotalItems(0);
      setLastPage(1);
    }
  }, [allData, searchQuery, applyFilters]);

  useEffect(() => {
    if (filteredData.length > 0) {
      const startIndex = (currentPage - 1) * perPage;
      const endIndex = startIndex + perPage;
      setTableData(filteredData.slice(startIndex, endIndex));
    }
  }, [currentPage, perPage, filteredData]);

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-700";
      case "normal":
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "low":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getStatusBadge = (status) => {
    return status?.toLowerCase() === "active"
      ? "bg-green-100 text-green-700"
      : "bg-gray-200 text-gray-600";
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Category name is required";
    if (!formData.description.trim()) errors.description = "Description is required";
    if (!formData.priority) errors.priority = "Priority level is required";
    if (!formData.status) errors.status = "Status is required";
    
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = () => {
    if (!validateForm()) return;

    const categoryData = {
      name: formData.name,
      description: formData.description,
      priority: formData.priority.toLowerCase(),
      status: formData.status.toLowerCase(),
    };

    createCategory(categoryData);
  };

  const handleEditClick = (category) => {
    setEditingCategory(category);
    setShowEditModal(true);
  };

  const handleUpdateSuccess = () => {
    setShowEditModal(false);
    setEditingCategory(null);
    refetch();
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      deleteCategory(id);
    }
  };

  const handleToggleStatus = (id, currentStatus) => {
    const newStatus = currentStatus?.toLowerCase() === "active" ? "inactive" : "active";
    
    const category = allData.find(cat => cat.id === id);
    if (!category) return;

    const updatedData = {
      name: category.name,
      description: category.description,
      priority: category.priority,
      status: newStatus
    };

    toggleStatus({ id, data: updatedData });
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      name: "",
      description: "",
      priority: "",
      status: "",
    });
    setCreateErrors({});
  };

  const columns = [
    {
      key: "name",
      label: "CATEGORY NAME",
      render: (_, cat) => (
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
          {cat?.name || "—"}
        </span>
      ),
    },
    {
      key: "description",
      label: "DESCRIPTION",
      render: (_, cat) => (
        <div className="group relative">
          <span className="text-sm text-gray-500 block max-w-xs truncate">
            {cat?.description || "—"}
          </span>
          {cat?.description && cat.description.length > 60 && (
            <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-10">
              <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 max-w-md whitespace-normal break-words">
                {cat.description}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "priority",
      label: "PRIORITY",
      render: (_, cat) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getPriorityBadge(cat?.priority)}`}
        >
          {cat?.priority || "—"}
        </span>
      ),
    },
    {
      key: "status",
      label: "STATUS",
      render: (_, cat) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadge(cat?.status)}`}
        >
          {cat?.status || "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "ACTIONS",
      render: (_, cat) => (
        <div className="flex gap-2 items-center whitespace-nowrap">
          <button
            onClick={() => handleEditClick(cat)}
            className="text-yellow-600 hover:text-yellow-900 p-1"
            title="Edit"
            disabled={isToggling || isDeleting}
          >
            <PencilIcon className="w-4 h-4" />
          </button>

          {/* Toggle Switch */}
          <button
            onClick={() => handleToggleStatus(cat?.id, cat?.status)}
            disabled={isToggling}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
              cat?.status?.toLowerCase() === "active" ? "bg-green-500" : "bg-gray-300"
            } ${isToggling ? "opacity-50 cursor-not-allowed" : ""}`}
            title={
              cat?.status?.toLowerCase() === "active"
                ? "Switch to Inactive"
                : "Switch to Active"
            }
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                cat?.status?.toLowerCase() === "active"
                  ? "translate-x-5"
                  : "translate-x-1"
              }`}
            />
          </button>

          <button
            onClick={() => handleDelete(cat?.id)}
            className="text-red-600 hover:text-red-900 p-1"
            title="Delete"
            disabled={isDeleting}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const handlePageChange = (page, newPerPage = perPage) => {
    setCurrentPage(page);
    if (newPerPage !== perPage) {
      setPerPage(newPerPage);
    }
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const createModalFooter = (
    <div className="flex justify-end gap-2">
      <button
        onClick={handleCloseCreateModal}
        className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
        disabled={isCreating}
      >
        Cancel
      </button>
      <button
        onClick={handleCreate}
        disabled={isCreating}
        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isCreating ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Creating...
          </>
        ) : (
          "Create"
        )}
      </button>
    </div>
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleBackdropClick}
        ></div>

        {/* Modal panel */}
        <div className="flex min-h-full items-start justify-center p-4">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900" id="modal-title">
                Complaint Categories
              </h3>
              <button
                onClick={onClose}
                className="rounded-lg p-1 hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Title, Search and Add Button */}
                <div className="flex items-center justify-between">
                  {/* Title with icon */}
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-200/50 rounded-full">
                      <svg
                        className="w-4 h-4 text-blue-500 m-2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 7h18M3 12h18M3 17h18"
                        />
                      </svg>
                    </div>
                    <p className="font-medium text-md">
                      List of Complaint Categories
                    </p>
                  </div>

                  {/* Search and Add Button */}
                  <div className="flex items-center gap-2">
                    <div className="w-64">
                      <FormInput
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="mb-0"
                      />
                    </div>

                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="bg-blue-600 text-white px-3 py-3 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1 whitespace-nowrap"
                    >
                      <PlusIcon className="w-4 h-4" />
                      New Category
                    </button>
                  </div>
                </div>

                {/* Table Container */}
                <div>
                  <Table
                    noContainer={true}
                    columns={columns}
                    data={tableData}
                    getRowKey={(cat) => cat?.id || `cat-${Math.random()}`}
                    isLoading={isLoading || isDeleting || isToggling}
                    emptyMessage="No complaint categories found"
                    pagination={{
                      currentPage: currentPage,
                      lastPage: lastPage,
                      perPage: perPage,
                      total: totalItems,
                    }}
                    onPageChange={handlePageChange}
                    paginationProps={{
                      showSizeSelector: true,
                      onPerPageChange: handlePerPageChange,
                      isLoading,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE MODAL */}
      <Modal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        size="md"
        title="New Category"
        footer={createModalFooter}
      >
        <div className="space-y-4 py-2">
          {/* Name */}
          <FormInput
            label="Category Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter category name"
            error={createErrors.name}
          />

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              placeholder="Enter brief description here"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="3"
              className={`w-full border-2 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                createErrors.description ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            {createErrors.description && (
              <p className="mt-1 text-xs text-red-500">{createErrors.description}</p>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority Level <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
              className={`w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                createErrors.priority ? 'border-red-500' : 'border-gray-200'
              }`}
            >
              <option value="">Select a priority level</option>
              <option value="High">High</option>
              <option value="Normal">Normal</option>
              <option value="Low">Low</option>
            </select>
            {createErrors.priority && (
              <p className="mt-1 text-xs text-red-500">{createErrors.priority}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className={`w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                createErrors.status ? 'border-red-500' : 'border-gray-200'
              }`}
            >
              <option value="">Select a status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            {createErrors.status && (
              <p className="mt-1 text-xs text-red-500">{createErrors.status}</p>
            )}
          </div>
        </div>
      </Modal>

      <EditCategoryModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onUpdate={handleUpdateSuccess}
      />
    </>
  );
};

export default ComplaintsCategoriesModal;